/**
 * Extract and normalize Instagram username from various input formats
 * Handles:
 * - Plain username: "username"
 * - Handle with @: "@username"
 * - Full URLs: "https://www.instagram.com/username/", "https://instagram.com/username"
 * - Short URLs: "instagram.com/username", "www.instagram.com/username"
 * - URLs with query params: "https://www.instagram.com/username/?hl=en"
 * - URLs with fragments: "https://www.instagram.com/username/#reels"
 * - URLs with mobile subdomain: "https://m.instagram.com/username"
 * - Instagram redirect links: "https://l.instagram.com/?u=<encoded-profile-url>"
 */
export function extractInstagramUsername(input: string): string | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  const trimmedInput = input.trim();

  // Instagram sometimes wraps copied profile URLs in l.instagram.com redirects.
  // Resolve only its explicit `u` target before stripping query/hash tracking so
  // the normal profile parser can canonicalize the destination handle.
  let redirectTarget = trimmedInput;
  try {
    const redirectUrl = new URL(
      trimmedInput.match(/^https?:\/\//i) ? trimmedInput : `https://${trimmedInput}`
    );
    if (redirectUrl.hostname.toLowerCase() === 'l.instagram.com') {
      redirectTarget = redirectUrl.searchParams.get('u') || '';
    }
  } catch {
    // Let the normal username validation below reject malformed non-URL input.
  }

  // Strip share-link tracking before extracting the profile path. Instagram's
  // native profile-share URLs append igsh/utm parameters (and can use its
  // internal /_u/ profile route), neither of which is part of a username.
  let normalized = redirectTarget.split(/[?#]/, 1)[0] || '';

  if (!normalized) {
    return null;
  }

  // Check if it's a URL pattern
  const urlPatterns = [
    // Full URLs with https/http
    /^https?:\/\/(?:www\.|m\.)?instagram\.com\/(?:_u\/)?([a-zA-Z0-9._]+)/i,
    // URLs without protocol
    /^(?:www\.|m\.)?instagram\.com\/(?:_u\/)?([a-zA-Z0-9._]+)/i,
    // Short Instagram links
    /^instagr\.am\/(?:_u\/)?([a-zA-Z0-9._]+)/i
  ];

  for (const pattern of urlPatterns) {
    const match = normalized.match(pattern);
    if (match && match[1]) {
      // Extract username from URL
      normalized = match[1];
      break;
    }
  }

  // Remove trailing slashes if any
  normalized = normalized.replace(/\/+$/, '');

  // Remove @ symbol if present
  normalized = normalized.replace(/^@+/, '');

  // Instagram username validation rules:
  // - Must be 1-30 characters
  // - Can contain letters, numbers, periods (.), and underscores (_)
  // - Cannot contain spaces or special characters
  // - Cannot start or end with period (underscores are allowed at start/end)
  // - Periods and underscores cannot be consecutive

  if (!normalized) {
    return null;
  }

  // Validate length
  if (normalized.length < 1 || normalized.length > 30) {
    return null;
  }

  // Validate characters (only letters, numbers, periods, underscores)
  if (!/^[a-zA-Z0-9._]+$/.test(normalized)) {
    return null;
  }

  // Cannot start or end with period (underscores are allowed)
  if (/^\.|\.$/.test(normalized)) {
    return null;
  }

  // Cannot have consecutive periods or underscores
  if (/[._]{2,}/.test(normalized)) {
    return null;
  }

  // Return lowercase (Instagram usernames are case-insensitive)
  return normalized.toLowerCase();
}

/**
 * Validate if a string is a valid Instagram username format
 */
export function isValidInstagramUsername(username: string): boolean {
  return extractInstagramUsername(username) !== null;
}
